import { useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useStores } from "../../../../stores/setup/use-store";
import { IOperationRuleLocation } from "../../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { LocationFormValues } from "../types";
import { StoreState } from "../../../../enum/StoreState";
import { FormikProps } from "formik";

export function useLocationOperations() {
	const {
		dataStores: {
			operationRuleStore,
			operationRuleLocationStore,
			operationRuleLocationInsightStore,
			oceanWeatherIntelligenceStore,
		},
	} = useStores();

	const toast = useToast();
	const { t } = useTranslation("translation", {
		keyPrefix: "oceanWeatherIntelligence.operationRuleLocation",
	});

	const handleOnEdit = useCallback(
		(
			value: IOperationRuleLocation,
			formikEdit: FormikProps<any>,
			onOpen: () => void,
			setIsEditing: (value: boolean) => void
		) => {
			if (
				!operationRuleStore.operationRuleMainId ||
				operationRuleStore.operationRuleMainId <= 0
			) {
				message.info(t("youNeedSelectDashboardToAddLocation"));
			} else {
				formikEdit.setFieldValue("name", value.name);
				formikEdit.setFieldValue("description", value.description);

				if (value.geofence && value.geofence.length > 0 && !value.locationId) {
					formikEdit.setFieldValue("geofence", value.geofence);
					formikEdit.setFieldValue("locationId", undefined);
					if (value.macroRegionId) {
						formikEdit.setFieldValue("macroRegionId", value.macroRegionId);
					}
				} else if (value.locationId && value.locationId > 0) {
					formikEdit.setFieldValue("locationId", value.locationId);
					if (value.geofence && value.geofence.length > 0) {
						formikEdit.setFieldValue("geofence", value.geofence);
					}
					if (value.macroRegionId) {
						formikEdit.setFieldValue("macroRegionId", value.macroRegionId);
					}
				} else if (value.stationId && value.regionId) {
					formikEdit.setFieldValue(
						"stationRegion",
						`${value.stationId}-${value.regionId}`
					);
					formikEdit.setFieldValue("regionId", value.regionId);
					formikEdit.setFieldValue("macroRegionId", value.macroRegionId);
				}

				setIsEditing(true);
				operationRuleLocationStore.setOperationRuleLocation(value);
				onOpen();
			}
		},
		[operationRuleStore, operationRuleLocationStore, t]
	);

	const handleOnConfirmDelete = useCallback(
		(locationRemove: IOperationRuleLocation | null, onDeleteClose: () => void, setLocationRemove: (value: IOperationRuleLocation | null) => void) => {
			const locationId = locationRemove?.id;

			if (locationId && locationId > 0) {
				operationRuleLocationStore
					.deleteOperationRuleLocation(locationId)
					.then((success) => {
						if (success) {
							if (!operationRuleLocationStore.operationRuleLocations?.length) {
								operationRuleLocationStore.setHasLocations(false);
								operationRuleLocationInsightStore.setHasInsights(false);
							} else {
								operationRuleLocationStore.setHasLocations(true);
							}
						}
					});

				oceanWeatherIntelligenceStore.setChangeForUpdate({
					update: true,
					expiration: 0,
				});

				if (
					operationRuleLocationStore.operationRuleLocationMainId === locationId
				) {
					operationRuleLocationStore.setOperationRuleLocationMainId(
						operationRuleLocationStore.operationRuleLocations?.length === 0
							? 0
							: operationRuleLocationStore.operationRuleLocations[0].id
					);
					operationRuleLocationInsightStore.reset();
					operationRuleLocationStore.setActionCallInsightsList();
				}

				setLocationRemove(null);
				onDeleteClose();
			} else {
				message.error(t("locationRemovalFailedContactUs"));
			}
		},
		[
			operationRuleLocationStore,
			operationRuleLocationInsightStore,
			oceanWeatherIntelligenceStore,
			t,
		]
	);

	const handleAddOrEditData = useCallback(
		(
			values: LocationFormValues,
			isEditing: boolean,
			onClose: () => void
		) => {
			const { name, description, stationRegion, macroRegionId, locationId, geofence } = values;

			if (locationId && locationId > 0) {
				if (!name) {
					toast({
						title: t("youNeedToFillAllTheFields"),
						status: "error",
						position: "top",
						isClosable: true,
					});
					return;
				}

				const editLocationId = operationRuleLocationStore.operationRuleLocation?.id;

				let operationRuleLocation = {
					id: 0,
					name: name,
					description: description || "",
					locationId: locationId,
					macroRegionId: macroRegionId,
					operationRuleId: operationRuleStore.operationRuleMainId,
				};

				operationRuleLocationStore.setOperationRuleLocation(
					operationRuleLocation
				);

				if (isEditing && editLocationId && editLocationId > 0) {
					operationRuleLocation = { ...operationRuleLocation, id: editLocationId };
					operationRuleLocationStore.setOperationRuleLocation(
						operationRuleLocation
					);
					operationRuleLocationStore.updateOperationRuleLocation().then(() => {
						if (operationRuleLocationStore.state !== StoreState.ERROR) {
							toast({
								title: t("locationSuccessOnSave"),
								status: "success",
								position: "bottom-right",
								isClosable: true,
							});
						}
					});
				} else {
					operationRuleLocationStore.addOperationRuleLocation().then(() => {
						if (operationRuleLocationStore.state !== StoreState.ERROR) {
							toast({
								title: t("locationSuccessOnSave"),
								status: "success",
								position: "bottom-right",
								isClosable: true,
							});
						}
					});
				}

				oceanWeatherIntelligenceStore.setChangeForUpdate({
					update: true,
					expiration: 0,
				});

				onClose();
				return;
			}

			if (geofence && geofence.length > 0) {
				if (!name) {
					toast({
						title: t("youNeedToFillAllTheFields"),
						status: "error",
						position: "top",
						isClosable: true,
					});
					return;
				}

				const editLocationId = operationRuleLocationStore.operationRuleLocation?.id;

				let operationRuleLocation = {
					id: 0,
					name: name,
					description: description || "",
					geofence: geofence,
					macroRegionId: macroRegionId,
					operationRuleId: operationRuleStore.operationRuleMainId,
				};

				operationRuleLocationStore.setOperationRuleLocation(
					operationRuleLocation
				);

				if (isEditing && editLocationId && editLocationId > 0) {
					operationRuleLocation = { ...operationRuleLocation, id: editLocationId };
					operationRuleLocationStore.setOperationRuleLocation(
						operationRuleLocation
					);
					operationRuleLocationStore.updateOperationRuleLocation().then(() => {
						if (operationRuleLocationStore.state !== StoreState.ERROR) {
							toast({
								title: t("locationSuccessOnSave"),
								status: "success",
								position: "bottom-right",
								isClosable: true,
							});
						}
					});
				} else {
					operationRuleLocationStore.addOperationRuleLocation().then(() => {
						if (operationRuleLocationStore.state !== StoreState.ERROR) {
							toast({
								title: t("locationSuccessOnSave"),
								status: "success",
								position: "bottom-right",
								isClosable: true,
							});
						}
					});
				}

				oceanWeatherIntelligenceStore.setChangeForUpdate({
					update: true,
					expiration: 0,
				});

				onClose();
				return;
			}

			const hasRequiredFields = name && stationRegion && macroRegionId;

			if (!hasRequiredFields) {
				toast({
					title: t("youNeedToFillAllTheFields"),
					status: "error",
					position: "top",
					isClosable: true,
				});
				return;
			}

			const stationRegionArray = stationRegion.split("-");
			if (stationRegionArray?.length !== 2) {
				return;
			}

			const stationId = stationRegionArray[0];
			const regionId = stationRegionArray[1];

			const stationEntity = oceanWeatherIntelligenceStore.getStationForLocation(
				parseInt(stationId),
				parseInt(regionId)
			);

			if (!stationEntity || !stationEntity.region_id) {
				toast({
					title: t("stationNotFoundPleaseSelectValidStation"),
					status: "error",
					position: "top",
					isClosable: true,
				});
				return;
			}

			const editLocationId = operationRuleLocationStore.operationRuleLocation?.id;

			let operationRuleLocation = {
				id: 0,
				name: name,
				description: description || "",
				stationId: parseInt(stationId.toString()),
				regionId: stationEntity.region_id,
				macroRegionId,
				operationRuleId: operationRuleStore.operationRuleMainId,
			};

			operationRuleLocationStore.setOperationRuleLocation(
				operationRuleLocation
			);

			if (isEditing && editLocationId && editLocationId > 0) {
				operationRuleLocation = { ...operationRuleLocation, id: editLocationId };
				operationRuleLocationStore.setOperationRuleLocation(
					operationRuleLocation
				);
				operationRuleLocationStore.updateOperationRuleLocation().then(() => {
					if (operationRuleLocationStore.state !== StoreState.ERROR) {
						toast({
							title: t("locationSuccessOnSave"),
							status: "success",
							position: "bottom-right",
							isClosable: true,
						});
					}
				});
			} else {
				operationRuleLocationStore.addOperationRuleLocation().then(() => {
					if (operationRuleLocationStore.state !== StoreState.ERROR) {
						toast({
							title: t("locationSuccessOnSave"),
							status: "success",
							position: "bottom-right",
							isClosable: true,
						});
					}
				});
			}

			oceanWeatherIntelligenceStore.setChangeForUpdate({
				update: true,
				expiration: 0,
			});

			onClose();
		},
		[
			operationRuleStore,
			operationRuleLocationStore,
			oceanWeatherIntelligenceStore,
			toast,
			t,
		]
	);

	return {
		handleOnEdit,
		handleOnConfirmDelete,
		handleAddOrEditData,
	};
}
